import { useState } from 'react';
import type { SelectedItem } from '../types/plant';
import { NODE_LABEL, SEGMENT_LABEL } from '../lib/plant-styles';
import LinkAnalysisPanel from './LinkAnalysisPanel';

type Props = {
  selected: SelectedItem;
  onDelete: () => void;
  onGenerateDrops: (buffer_m: number, max_drop_m: number) => void;
  generatingDrops: boolean;
  lastDropResult: { created: number; skipped: number; considered: number } | null;
};

export default function InspectorPanel({ selected, onDelete, onGenerateDrops, generatingDrops, lastDropResult }: Props) {
  // Tower link analysis state: click first tower → set as A, click second → set as B.
  const [linkTowerA, setLinkTowerA] = useState<{ lat: number; lng: number; height_m: number; label?: string } | null>(null);
  const [linkTowerB, setLinkTowerB] = useState<{ lat: number; lng: number; height_m: number; label?: string } | null>(null);

  // When a tower is selected, auto-assign to A or B.
  // FCC ASR data: column names say "_ft" but values are actually METERS (AMSL).
  //   height_ft  = ground elevation AMSL (meters)
  //   overall_ft = tower-top elevation AMSL (meters)
  //   Tower height AGL = overall_ft - height_ft
  const currentTower = selected?.kind === 'tower' ? (() => {
    const groundM = Number(selected.feature.properties.height_ft ?? 0);
    const topM = Number(selected.feature.properties.overall_ft ?? 0);
    const aglM = Math.max(10, topM - groundM); // above-ground in meters
    return {
      lat: (selected.feature.geometry as any)?.coordinates?.[1] ?? 0,
      lng: (selected.feature.geometry as any)?.coordinates?.[0] ?? 0,
      height_m: Math.round(aglM),
      label: `${selected.feature.properties.city ?? 'Tower'} (${selected.feature.properties.reg_num ?? '?'})`,
    };
  })() : null;
  return (
    <div className="card" style={{ height: '100%', overflow: 'auto' }}>
      <div className="card__header">
        {selected
          ? (selected.kind === 'node'
              ? `Node · ${selected.feature.properties.auto_label}`
              : selected.kind === 'segment'
                ? `Segment · ${selected.feature.properties.auto_label}`
                : selected.kind === 'drop'
                  ? 'Drop'
                  : selected.kind === 'tower'
                    ? `Tower · ${selected.feature.properties.city ?? 'Unknown'}`
                    : 'Building')
          : 'Inspector'}
      </div>
      <div className="card__body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!selected && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Nothing selected. Click a node, segment, or building on the map to inspect.
          </div>
        )}

        {selected?.kind === 'node' && (
          <>
            <Row label="Type"   value={NODE_LABEL[selected.feature.properties.type] ?? selected.feature.properties.type} />
            <Row label="ID"     value={<code>{selected.feature.properties.auto_label}</code>} />
            {selected.feature.properties.label && (
              <Row label="Label" value={selected.feature.properties.label} />
            )}
            {selected.feature.properties.splitter_ratio && (
              <Row label="Ratio" value={selected.feature.properties.splitter_ratio} />
            )}
            {selected.feature.properties.capacity != null && (
              <Row label="Capacity" value={selected.feature.properties.capacity} />
            )}
            <Row label="Status" value={<Pill status={selected.feature.properties.status} />} />
            <Row label="Coord"  value={
              <code style={{ fontSize: 11 }}>
                {selected.feature.geometry.coordinates[1].toFixed(5)}, {selected.feature.geometry.coordinates[0].toFixed(5)}
              </code>
            } />
            <button
              className="btn"
              onClick={onDelete}
              style={{ background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5', marginTop: 8 }}
              title="Delete this node (Del or Backspace)"
            >
              🗑 Delete node
            </button>
            <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>Or press <kbd>Del</kbd></div>
          </>
        )}

        {selected?.kind === 'segment' && (
          <>
            <Row label="Type"       value={SEGMENT_LABEL[selected.feature.properties.type] ?? selected.feature.properties.type} />
            <Row label="ID"         value={<code>{selected.feature.properties.auto_label}</code>} />
            <Row label="Placement"  value={selected.feature.properties.placement.replace('_',' ')} />
            <Row label="Length"     value={`${Math.round(selected.feature.properties.length_m * 3.28084).toLocaleString()} ft`} />
            {selected.feature.properties.cable_type && (
              <Row label="Cable" value={selected.feature.properties.cable_type} />
            )}
            {selected.feature.properties.strand_count != null && (
              <Row label="Strands" value={selected.feature.properties.strand_count} />
            )}
            <Row label="Status"     value={<Pill status={selected.feature.properties.status} />} />

            {(selected.feature.properties.type === 'distribution' ||
              selected.feature.properties.type === 'feeder_trunk' ||
              selected.feature.properties.type === 'backbone') && (
              <div style={{ marginTop: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-alt)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Auto-drop to nearby buildings</div>
                <AutoDropControls
                  onGenerate={onGenerateDrops}
                  generating={generatingDrops}
                  lastResult={lastDropResult}
                />
              </div>
            )}
            <button
              className="btn"
              onClick={onDelete}
              style={{ background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5', marginTop: 8 }}
              title="Delete this segment (Del or Backspace)"
            >
              🗑 Delete segment
            </button>
            <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>Or press <kbd>Del</kbd></div>
          </>
        )}

        {selected?.kind === 'drop' && (
          <>
            <Row label="Status" value={<Pill status={selected.feature.properties.status} />} />
            <Row label="Units"  value={selected.feature.properties.units} />
            <Row label="Length" value={`${Math.round(selected.feature.properties.length_m * 3.28084)} ft`} />
            <Row label="GeoHub ID" value={<code>{selected.feature.properties.building_geohub_id}</code>} />
          </>
        )}

        {selected?.kind === 'building' && (
          <>
            <Row label="GeoHub ID" value={<code>{selected.feature.properties.id}</code>} />
            <Row label="State"     value={selected.feature.properties.state} />
            <Row label="Served"    value={selected.feature.properties.served ? 'Yes' : 'No'} />
          </>
        )}

        {selected?.kind === 'tower' && (
          <>
            <div style={{
              padding: 8, background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: 6, fontSize: 11, fontWeight: 700, color: '#dc2626',
              textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 4,
            }}>
              FCC ASR Tower
            </div>
            <Row label="City" value={selected.feature.properties.city} />
            <Row label="State" value={selected.feature.properties.state} />
            <Row label="Registration #" value={<code>{selected.feature.properties.reg_num}</code>} />
            <Row label="Height AGL" value={`${Math.round(selected.feature.properties.overall_ft - selected.feature.properties.height_ft)} m (${Math.round((selected.feature.properties.overall_ft - selected.feature.properties.height_ft) * 3.28084)} ft)`} />
            <Row label="Ground elev." value={`${Math.round(selected.feature.properties.height_ft)} m (${Math.round(selected.feature.properties.height_ft * 3.28084)} ft AMSL)`} />
            <Row label="Type" value={selected.feature.properties.structure_type} />
            <Row label="Status" value={selected.feature.properties.status_code === 'A' ? 'Constructed' : selected.feature.properties.status_code === 'C' ? 'Constructed' : selected.feature.properties.status_code} />

            {/* PTP link assignment buttons */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button
                className="btn"
                onClick={() => currentTower && setLinkTowerA(currentTower)}
                style={{
                  flex: 1, fontSize: 11,
                  background: linkTowerA?.label === currentTower?.label ? '#dbeafe' : undefined,
                  borderColor: linkTowerA?.label === currentTower?.label ? '#3b82f6' : undefined,
                }}
              >
                Set as Tower A
              </button>
              <button
                className="btn"
                onClick={() => currentTower && setLinkTowerB(currentTower)}
                style={{
                  flex: 1, fontSize: 11,
                  background: linkTowerB?.label === currentTower?.label ? '#dbeafe' : undefined,
                  borderColor: linkTowerB?.label === currentTower?.label ? '#3b82f6' : undefined,
                }}
              >
                Set as Tower B
              </button>
            </div>

            {/* Link analysis panel */}
            <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <LinkAnalysisPanel
                towerA={linkTowerA}
                towerB={linkTowerB}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function Pill({ status }: { status: string }) {
  return <span className={`pill ${statusClass(status)}`}>{status.replace('_', ' ')}</span>;
}
function statusClass(s: string): string {
  if (/built|installed|in_service|tested/.test(s)) return 'pill--success';
  if (/in_progress|permitting/.test(s))             return 'pill--warning';
  if (/abandoned|retired|denied/.test(s))           return 'pill--danger';
  return 'pill--info';
}

function AutoDropControls({
  onGenerate, generating, lastResult,
}: {
  onGenerate: (buffer_m: number, max_drop_m: number) => void;
  generating: boolean;
  lastResult: { created: number; skipped: number; considered: number } | null;
}) {
  const [buffer, setBuffer] = useState(30);
  const [maxDrop, setMaxDrop] = useState(90);
  return (
    <>
      <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
        <label style={{ flex: 1 }}>
          Buffer (m)
          <input type="number" min={5} max={200} value={buffer}
                 onChange={(e) => setBuffer(Number(e.target.value))}
                 style={{ width: '100%', padding: 4, border: '1px solid var(--border-strong)', borderRadius: 4 }} />
        </label>
        <label style={{ flex: 1 }}>
          Max drop (m)
          <input type="number" min={10} max={300} value={maxDrop}
                 onChange={(e) => setMaxDrop(Number(e.target.value))}
                 style={{ width: '100%', padding: 4, border: '1px solid var(--border-strong)', borderRadius: 4 }} />
        </label>
      </div>
      <button
        className="btn btn--primary"
        style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
        disabled={generating}
        onClick={() => onGenerate(buffer, maxDrop)}
      >
        {generating ? 'Generating…' : '✨ Generate drops'}
      </button>
      {lastResult && (
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
          Considered {lastResult.considered}, created {lastResult.created}, skipped {lastResult.skipped}.
        </div>
      )}
    </>
  );
}

